import { jsonBody, requestJson } from "../http/httpClient";
import { readObject, readString } from "../http/jsonDecoding";
import { readReview, type Review } from "./reviewDecoders";
import type { VersionBump } from "./versionDecoders";

/**
 * Reviews of change sets (`docs/components/reviews.md`): read one, decide on exactly the
 * head the curator saw, and merge with an acknowledgement of what is open. Lists come with
 * the inbox (`reviewCollaborationApi`).
 * Every write names the head, so a decision on a head that moved is refused as stale.
 */

export type DecisionRequestKind = "approve" | "request_changes" | "scrap";

export interface MergeAcknowledgement {
  readonly checkRunId: string | null;
  readonly conflictCheckId: string | null;
  readonly note: string;
}

const changeSetPath = (changeSetId: string): `/${string}` =>
  `/content/change-sets/${encodeURIComponent(changeSetId)}`;

export function getReview(changeSetId: string, signal?: AbortSignal): Promise<Review> {
  return requestJson({ method: "GET", path: `${changeSetPath(changeSetId)}/review`, signal, decodeResponse: readReview });
}

export function decideOnReview(
  changeSetId: string,
  decision: { readonly kind: DecisionRequestKind; readonly reviewedHead: string; readonly comment: string },
): Promise<Review> {
  return requestJson({
    method: "POST",
    path: `${changeSetPath(changeSetId)}/decisions`,
    body: jsonBody({ kind: decision.kind, reviewed_head: decision.reviewedHead, comment: decision.comment }),
    decodeResponse: readReview,
  });
}

export function mergeReview(
  changeSetId: string,
  merge: {
    readonly reviewedHead: string;
    readonly reason: string;
    readonly acknowledgement: MergeAcknowledgement | null;
    /** The change line the new version labels show. */
    readonly changeLine: string;
    /** For the project content as a whole; `null` leaves DMW's proposal. */
    readonly versionBump: VersionBump | null;
    /** For each modified item; an item left out keeps DMW's proposal. */
    readonly itemBumps: Readonly<Record<string, VersionBump>>;
  },
): Promise<Review> {
  return requestJson({
    method: "POST",
    path: `${changeSetPath(changeSetId)}/merge`,
    body: jsonBody({
      reviewed_head: merge.reviewedHead,
      reason: merge.reason,
      acknowledgement:
        merge.acknowledgement === null
          ? null
          : {
              check_run_id: merge.acknowledgement.checkRunId,
              conflict_check_id: merge.acknowledgement.conflictCheckId,
              note: merge.acknowledgement.note,
            },
      release_note: merge.changeLine,
      version_bump: merge.versionBump,
      item_bumps: merge.itemBumps,
    }),
    decodeResponse: readReview,
  });
}

/** Run the conflict check on the head again; returns the new run's ID. */
export function rerunConflictCheck(changeSetId: string): Promise<string> {
  return requestJson({
    method: "POST",
    path: `${changeSetPath(changeSetId)}/conflict-checks`,
    decodeResponse: (body) => readString(readObject(body, "conflict check"), "conflict_check_id"),
  });
}

/** Resubmit a change set that was sent back; it is checked again. */
export function resubmitChangeSet(changeSetId: string, reason: string): Promise<void> {
  return requestJson({
    method: "POST",
    path: `${changeSetPath(changeSetId)}/resubmit`,
    body: jsonBody({ reason }),
    decodeResponse: () => undefined,
  });
}

